import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PerProtocolLink, ProductBadge } from "@perprotocol/ui";
import { brand } from "./brand";
import { evaluateGrade } from "./evaluator";
import { findDeepLink, findEquivalentTerm, getTerm, loadTerms, termPath } from "./repository";
import { encoderQueryKey, matchLabel, normalizeSearch, parseQuery, searchCtcae } from "./search";
import { createSearchRecorder } from "./searchLogging";
import { addRecent, loadLocalState, setPreferredVersion, toggleSaved } from "./storage";
import type { CtcaeTerm, CtcaeVersion, MatchType, ReferenceKey, ReferenceValues, SearchResult } from "./types";
import { BrowsePanel } from "./components/BrowsePanel";
import { PdfModal, type PdfTarget } from "./components/PdfModal";
import { ResultDetail } from "./components/ResultDetail";
import { SearchStatusIndicator, type SearchIndicatorState } from "./components/SearchStatusIndicator";
import { TermDialog } from "./components/TermDialog";
import { EncoderClient } from "./model-lab/encoderClient";
import { PRECOMPUTED_INDEXES, PRODUCTION_MODEL } from "./model-lab/models";
import { encoderDocument, mergeSearchResults } from "./model-lab/retrieval";
import { encoderDebounceMs, MIN_ENCODER_DEBOUNCE_MS } from "./model-lab/timing";
import type { EncoderMatch, WorkerResponse } from "./model-lab/types";

const generalGrades = {
  "5.0": [
    "Mild; asymptomatic or mild symptoms; clinical or diagnostic observations only; intervention not indicated.",
    "Moderate; minimal, local or noninvasive intervention indicated; limiting age-appropriate instrumental ADL.",
    "Severe or medically significant but not immediately life-threatening; hospitalization or prolongation of hospitalization indicated; disabling; limiting self care ADL.",
    "Life-threatening consequences; urgent intervention indicated.",
    "Death related to AE.",
  ],
  "6.0": [
    "Mild; asymptomatic or mild symptoms; clinical or diagnostic observations only; intervention not indicated.",
    "Moderate; minimal, local or noninvasive intervention indicated; limiting instrumental ADL or mild/moderate impact on age-appropriate normal daily activity (pediatric).",
    "Severe or medically significant but not immediately life-threatening; hospitalization or prolongation of hospitalization indicated; disabling; limiting self-care ADL or severe impact on age-appropriate normal daily activity (pediatric).",
    "Life-threatening consequences; urgent intervention indicated.",
    "Death related to AE.",
  ],
} satisfies Record<CtcaeVersion, string[]>;

function isModelArtifactFile(file?: string) {
  return Boolean(file && /\.onnx(?:_data)?$/i.test(file));
}

function resetSearchIndicator(current: SearchIndicatorState): SearchIndicatorState {
  return current.phase === "load-error" || current.phase === "waiting" || current.phase === "downloading"
    ? current
    : { phase: "idle" };
}

export default function App() {
  const initialState = useMemo(() => loadLocalState(), []);
  const [version, setVersion] = useState<CtcaeVersion>(() => {
    const pathVersion = window.location.pathname.match(/^\/ctcae\/(5\.0|6\.0)\//)?.[1];
    return pathVersion === "5.0" || pathVersion === "6.0" ? pathVersion : initialState.preferredVersion;
  });
  const [recordsByVersion, setRecordsByVersion] = useState<Partial<Record<CtcaeVersion, CtcaeTerm[]>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [resultsOpen, setResultsOpen] = useState(false);
  const [activeResult, setActiveResult] = useState(0);
  const [selectedId, setSelectedId] = useState<string>();
  const [versionSwitchTerm, setVersionSwitchTerm] = useState<CtcaeTerm>();
  const [selectedMatch, setSelectedMatch] = useState<MatchType>("exact");
  const [measurement, setMeasurement] = useState<{ value: number; unit?: string }>();
  const [localState, setLocalState] = useState(initialState);
  const [references, setReferences] = useState<ReferenceValues>({});
  const [referenceHistory, setReferenceHistory] = useState<string[]>([]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [pdfTarget, setPdfTarget] = useState<PdfTarget>();
  const [semanticMatches, setSemanticMatches] = useState<EncoderMatch[]>([]);
  const [semanticIndexes, setSemanticIndexes] = useState<Partial<Record<CtcaeVersion, boolean>>>({});
  const [semanticStatus, setSemanticStatus] = useState("Preparing smart search…");
  const [searchIndicator, setSearchIndicator] = useState<SearchIndicatorState>({
    phase: "waiting",
    label: "Smart search model is loading.",
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const headerTitleRef = useRef<HTMLElement>(null);
  const titleMotionRef = useRef<{ source: DOMRect; target: "header" | "hero" }>();
  const encoderRef = useRef<EncoderClient>();
  const encoderReadyRef = useRef(false);
  const encoderIndexRequests = useRef(new Set<CtcaeVersion>());
  const latestSemanticRequest = useRef(0);
  const semanticDebounceMs = useRef(MIN_ENCODER_DEBOUNCE_MS);
  const versionRef = useRef(version);
  const recordsRef = useRef(recordsByVersion);
  const queryRef = useRef(query);
  const searchRecorderRef = useRef<ReturnType<typeof createSearchRecorder>>();
  if (!searchRecorderRef.current) searchRecorderRef.current = createSearchRecorder();
  const currentRecords = recordsByVersion[version] ?? [];

  useEffect(() => { versionRef.current = version; }, [version]);
  useEffect(() => { recordsRef.current = recordsByVersion; }, [recordsByVersion]);
  useEffect(() => { queryRef.current = query; }, [query]);
  useEffect(() => () => searchRecorderRef.current?.cancel(), []);

  const captureTitleMotion = useCallback((target: "header" | "hero") => {
    const source = target === "header" ? heroTitleRef.current : headerTitleRef.current;
    if (!source || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      titleMotionRef.current = undefined;
      return;
    }
    titleMotionRef.current = { source: source.getBoundingClientRect(), target };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadTerms(version)
      .then((records) => {
        if (cancelled) return;
        setRecordsByVersion((current) => ({ ...current, [version]: records }));
        setError("");
        const linked = findDeepLink(records);
        if (linked) {
          captureTitleMotion("header");
          setSelectedId(linked.id);
          setSelectedMatch("exact");
          setQuery(linked.term);
          setDebouncedQuery(linked.term);
        }
      })
      .catch((reason: Error) => !cancelled && setError(reason.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [version]);

  useEffect(() => {
    const alternate: CtcaeVersion = version === "6.0" ? "5.0" : "6.0";
    const timer = window.setTimeout(() => {
      loadTerms(alternate).then((records) => setRecordsByVersion((current) => ({ ...current, [alternate]: records }))).catch(() => undefined);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [version]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), semanticDebounceMs.current);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const client = new EncoderClient();
    encoderRef.current = client;
    const unsubscribe = client.subscribe((message: WorkerResponse) => {
      if (message.type === "model-progress") {
        if (!isModelArtifactFile(message.file)) return;
        if (message.status === "download") {
          setSearchIndicator({ phase: "downloading", progress: 0 });
          setSemanticStatus("Downloading smart search model…");
        } else if (message.status === "progress" && message.progress !== undefined) {
          setSearchIndicator({
            phase: "downloading",
            progress: message.progress,
            loaded: message.loaded,
            total: message.total,
          });
        } else if (message.status === "done") {
          setSearchIndicator((current) => current.phase === "downloading"
            ? { phase: "waiting", label: "Smart search model is loading." }
            : current);
          setSemanticStatus("Preparing smart search…");
        }
      } else if (message.type === "runtime-fallback") {
        const retry = message.toDevice === "wasm" && message.toWasmThreads === 1
          ? "Retrying smart search with one CPU thread."
          : "Retrying smart search on this browser.";
        setSearchIndicator({ phase: "waiting", label: retry });
        setSemanticStatus(retry);
      } else if (message.type === "model-ready") {
        encoderReadyRef.current = true;
        semanticDebounceMs.current = encoderDebounceMs(message.integrity.verificationMs);
        setSearchIndicator((current) => current.phase === "waiting" || current.phase === "downloading" || current.phase === "load-error"
          ? { phase: "waiting", label: "Preparing smart search." }
          : current);
        setSemanticStatus(`Smart search loaded (${message.resolvedDevice}/${message.resolvedDtype})`);
        const currentVersion = versionRef.current;
        const records = recordsRef.current[currentVersion];
        if (records && !encoderIndexRequests.current.has(currentVersion)) {
          encoderIndexRequests.current.add(currentVersion);
          client.index(
            currentVersion,
            records.map((record) => ({ id: record.id, code: record.code, text: encoderDocument(record) })),
            PRECOMPUTED_INDEXES[currentVersion],
          );
        }
      } else if (message.type === "index-invalidated") {
        setSemanticStatus(`Refreshing an invalid ${message.source} index…`);
      } else if (message.type === "index-ready") {
        setSemanticIndexes((current) => ({ ...current, [message.version]: true }));
        setSearchIndicator((current) => current.phase === "waiting" || current.phase === "load-error"
          ? { phase: "idle" }
          : current);
        setSemanticStatus(`Smart search ready (${message.source} index)`);
      } else if (message.type === "search-started") {
        if (message.requestId !== latestSemanticRequest.current || message.version !== versionRef.current) return;
        setSearchIndicator({ phase: "working", label: "Smart search is working." });
        setSemanticStatus("Smart search is working…");
      } else if (message.type === "search-result") {
        semanticDebounceMs.current = encoderDebounceMs(message.diagnostics.inferenceMs);
        if (message.requestId !== latestSemanticRequest.current || message.version !== versionRef.current) return;
        setSemanticMatches(message.matches);
        if (message.matches.length) {
          setSearchIndicator({ phase: "idle" });
          setSemanticStatus("Smart search ready");
        } else {
          const searchedFor = parseQuery(queryRef.current).termQuery.trim();
          setSearchIndicator({
            phase: "no-results",
            title: "Smart search found no results",
            detail: searchedFor
              ? `The model returned no candidates for “${searchedFor}”.`
              : "The model returned no candidates for this search.",
            help: "Source-based search is still available.",
          });
          setSemanticStatus("Smart search found no results");
        }
      } else if (message.type === "error") {
        // Semantic retrieval is additive. The source-derived lexical search
        // remains fully usable if a browser cannot run the encoder.
        if (message.operation === "search") {
          if (message.requestId !== latestSemanticRequest.current) return;
          setSearchIndicator({
            phase: "inference-error",
            title: "Smart search could not finish",
            detail: message.message,
            help: "Source-based search is still available.",
          });
        } else {
          encoderReadyRef.current = false;
          setSearchIndicator({
            phase: "load-error",
            title: "Smart search did not load",
            detail: message.message,
            help: "Source-based search is still available.",
          });
        }
        setSemanticMatches([]);
        setSemanticStatus(`Smart search unavailable; using source search (${message.message})`);
      }
    });
    client.configure(PRODUCTION_MODEL);
    return () => {
      unsubscribe();
      client.dispose();
      encoderRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!encoderReadyRef.current || semanticIndexes[version] || encoderIndexRequests.current.has(version)) return;
    const records = recordsByVersion[version];
    if (!records) return;
    encoderIndexRequests.current.add(version);
    encoderRef.current?.index(
      version,
      records.map((record) => ({ id: record.id, code: record.code, text: encoderDocument(record) })),
      PRECOMPUTED_INDEXES[version],
    );
  }, [recordsByVersion, semanticIndexes, version]);

  const parsedQuery = useMemo(() => parseQuery(debouncedQuery), [debouncedQuery]);
  const lexicalResults = useMemo(() => searchCtcae(currentRecords, debouncedQuery, 16), [currentRecords, debouncedQuery]);
  const results = useMemo(
    () => mergeSearchResults(currentRecords, lexicalResults, semanticMatches, 8),
    [currentRecords, lexicalResults, semanticMatches],
  );
  const selected = selectedId ? getTerm(currentRecords, selectedId) : undefined;
  const unavailableTerm = versionSwitchTerm && recordsByVersion[version]
    && !findEquivalentTerm(currentRecords, versionSwitchTerm)
    ? versionSwitchTerm
    : undefined;
  const hasResultContext = Boolean(selected || versionSwitchTerm);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (searchWrapRef.current?.contains(target) || searchResultsRef.current?.contains(target)) return;
      setResultsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    if (!versionSwitchTerm || !recordsByVersion[version]) return;
    const equivalent = findEquivalentTerm(currentRecords, versionSwitchTerm);
    if (!equivalent) return;
    setSelectedId(equivalent.id);
    setSelectedMatch("exact");
    setQuery(equivalent.term);
    setDebouncedQuery(equivalent.term);
    setVersionSwitchTerm(undefined);
    window.history.replaceState({}, "", termPath(equivalent));
  }, [currentRecords, recordsByVersion, version, versionSwitchTerm]);

  useEffect(() => {
    const semanticQuery = parsedQuery.termQuery.trim();
    if (normalizeSearch(semanticQuery).length < 2 || !semanticIndexes[version]) {
      latestSemanticRequest.current = 0;
      setSemanticMatches([]);
      setSearchIndicator(resetSearchIndicator);
      return;
    }
    const requestId = encoderRef.current?.search(version, semanticQuery, 16) ?? 0;
    latestSemanticRequest.current = requestId;
    if (requestId) {
      setSearchIndicator({ phase: "working", label: "Smart search is working." });
      setSemanticStatus("Smart search is working…");
    }
  }, [parsedQuery.termQuery, semanticIndexes, version]);

  useLayoutEffect(() => {
    const motion = titleMotionRef.current;
    if (!motion) return;
    const target = motion.target === "header" ? headerTitleRef.current : heroTitleRef.current;
    if (!target) return;
    const destination = target.getBoundingClientRect();
    const scale = motion.source.height / destination.height;
    target.animate(
      [
        {
          transform: `translate(${motion.source.left - destination.left}px, ${motion.source.top - destination.top}px) scale(${scale})`,
          transformOrigin: "top left",
        },
        { transform: "translate(0, 0) scale(1)", transformOrigin: "top left" },
      ],
      { duration: 420, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
    titleMotionRef.current = undefined;
  }, [selectedId]);

  useEffect(() => {
    if (versionSwitchTerm) return;
    if (normalizeSearch(parsedQuery.termQuery).length < 3) {
      return;
    }
    if (!debouncedQuery || !results.length) {
      return;
    }
    const best = results[0];
    const autoSelectable = !["source-content", "source-routing", "other-specify"].includes(best.matchType);
    if (autoSelectable && best.score >= 58) {
      // Once the result layout is established, keep it mounted while the user
      // edits the query. A short or temporarily unmatched query should not flash
      // the home hero, and a replacement result should not replay hero motion.
      if (!selectedId) captureTitleMotion("header");
      setSelectedId(best.record.id);
      setSelectedMatch(best.matchType);
      window.history.replaceState({}, "", termPath(best.record));
    }
  }, [captureTitleMotion, debouncedQuery, parsedQuery.termQuery, results, selectedId, versionSwitchTerm]);

  useEffect(() => {
    if (!selected) return;
    setLocalState(addRecent({ recordId: selected.id, version: selected.version }));
  }, [selected?.id]);

  useEffect(() => setReferences({}), [selected?.id, measurement?.value]);

  const evaluation = useMemo(
    () => selected ? evaluateGrade(selected, measurement?.value, measurement?.unit, references) : {},
    [selected, measurement?.value, measurement?.unit, references],
  );

  const gradingQuery = useMemo(() => ({
    ...parsedQuery,
    numericValue: measurement?.value,
    unit: measurement?.unit,
  }), [parsedQuery, measurement]);

  const openReference = useCallback((id: string) => {
    setReferenceHistory((history) => [...history, id]);
  }, []);
  const closeReferences = useCallback(() => setReferenceHistory([]), []);
  const closePdf = useCallback(() => setPdfTarget(undefined), []);
  const referenceRecords = referenceHistory
    .map((id) => getTerm(currentRecords, id))
    .filter((record): record is CtcaeTerm => Boolean(record));

  const selectRecord = useCallback((record: CtcaeTerm, matchType: MatchType = "exact", keepTypedValue = false) => {
    if (!selectedId) captureTitleMotion("header");
    const typedQuery = keepTypedValue ? parseQuery(query) : undefined;
    const typedMeasurement = typedQuery?.numericValue !== undefined
      ? { value: typedQuery.numericValue, unit: typedQuery.unit }
      : undefined;
    if (record.version !== version) {
      setVersion(record.version);
      setPreferredVersion(record.version);
    }
    const nextQuery = typedMeasurement ? query.trim() : record.term;
    setQuery(nextQuery);
    setDebouncedQuery(nextQuery);
    setSelectedId(record.id);
    setVersionSwitchTerm(undefined);
    setSelectedMatch(matchType);
    setMeasurement(typedMeasurement);
    setResultsOpen(false);
    setBrowseOpen(false);
    window.history.pushState({}, "", termPath(record));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [captureTitleMotion, query, selectedId, version]);

  const changeVersion = (next: CtcaeVersion) => {
    if (next === version) return;
    if (query.trim()) searchRecorderRef.current?.schedule(query, next);
    const termContext = selected ?? versionSwitchTerm;
    const equivalent = termContext
      ? findEquivalentTerm(recordsByVersion[next] ?? [], termContext)
      : undefined;
    if (termContext) {
      const nextQuery = equivalent?.term ?? termContext.term;
      setQuery(nextQuery);
      setDebouncedQuery(nextQuery);
    }
    setVersion(next);
    setPreferredVersion(next);
    setSelectedId(equivalent?.id);
    setSelectedMatch("exact");
    setVersionSwitchTerm(equivalent ? undefined : termContext);
    latestSemanticRequest.current = 0;
    setSemanticMatches([]);
    setSearchIndicator(resetSearchIndicator);
    setMeasurement(undefined);
    setReferences({});
    setResultsOpen(false);
    if (equivalent) window.history.pushState({}, "", termPath(equivalent));
    else if (termContext) window.history.pushState({}, "", "/");
  };

  const clearSearch = useCallback(() => {
    searchRecorderRef.current?.cancel();
    if (selectedId) captureTitleMotion("hero");
    setQuery("");
    setDebouncedQuery("");
    setSelectedId(undefined);
    setVersionSwitchTerm(undefined);
    latestSemanticRequest.current = 0;
    setSemanticMatches([]);
    setSearchIndicator(resetSearchIndicator);
    setMeasurement(undefined);
    setResultsOpen(false);
    window.history.pushState({}, "", "/");
    searchRef.current?.focus();
  }, [captureTitleMotion, selectedId]);

  useEffect(() => {
    const onPopState = () => {
      const pathVersion = window.location.pathname.match(/^\/ctcae\/(5\.0|6\.0)\//)?.[1] as CtcaeVersion | undefined;
      if (pathVersion && pathVersion !== version) {
        setVersion(pathVersion);
        return;
      }
      const linked = findDeepLink(currentRecords);
      if (linked) {
        if (!selectedId) captureTitleMotion("header");
        setQuery(linked.term);
        setDebouncedQuery(linked.term);
        setSelectedId(linked.id);
        setVersionSwitchTerm(undefined);
        setSelectedMatch("exact");
        setMeasurement(undefined);
      } else {
        if (selectedId) captureTitleMotion("hero");
        setQuery("");
        setDebouncedQuery("");
        setSelectedId(undefined);
        setVersionSwitchTerm(undefined);
        setMeasurement(undefined);
        setResultsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const editing = ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable;
      if (event.key === "/" && !editing) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("popstate", onPopState);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentRecords, selectedId, version]);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && results.length) {
      event.preventDefault();
      setResultsOpen(true);
      setActiveResult((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp" && results.length) {
      event.preventDefault();
      setActiveResult((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      const immediateResults = normalizeSearch(query) === normalizeSearch(debouncedQuery)
        ? results
        : searchCtcae(currentRecords, query);
      const result = immediateResults[Math.min(activeResult, immediateResults.length - 1)];
      if (result) {
        event.preventDefault();
        searchRecorderRef.current?.select(query, version, result);
        selectRecord(result.record, result.matchType, true);
      }
    } else if (event.key === "Escape") {
      if (resultsOpen) setResultsOpen(false);
      else clearSearch();
    }
  };

  const resolveStored = (item: { recordId: string; version: CtcaeVersion }) =>
    getTerm(recordsByVersion[item.version] ?? [], item.recordId);
  const savedRecords = localState.savedTerms.map(resolveStored).filter((item): item is CtcaeTerm => Boolean(item));
  const recentRecords = localState.recentTerms.map(resolveStored).filter((item): item is CtcaeTerm => Boolean(item));
  const isSaved = selected ? localState.savedTerms.some((item) => item.recordId === selected.id) : false;

  const toggleCurrentSaved = () => {
    if (!selected) return;
    setLocalState(toggleSaved({ recordId: selected.id, version: selected.version }));
  };

  const resultForSelected: SearchResult | undefined = results.find((result) => result.record.id === selected?.id);
  const effectiveMatch = resultForSelected?.matchType ?? selectedMatch;
  const effectiveSearchIndicator: SearchIndicatorState = error
    ? {
        phase: "load-error",
        title: "CTCAE data did not load",
        detail: error,
        help: "Reload the page to try loading the source data again.",
      }
    : searchIndicator;

  return (
    <div className={`app${hasResultContext ? " has-result" : ""}`}>
      <header className="app-header">
        <a className="brand-lockup" href="/" onClick={(event) => { event.preventDefault(); clearSearch(); }} aria-label={`${brand.product} home`}>
          <strong ref={headerTitleRef}>{brand.product}</strong>
          <ProductBadge className="beta-badge">Beta</ProductBadge>
        </a>
        <PerProtocolLink className="about-link" label="perprotocol.org" />
      </header>

      <main>
        {!hasResultContext && (
          <section className="hero">
            <span className="eyebrow">Smart CTCAE search</span>
            <h1 ref={heroTitleRef}>{brand.product}</h1>
            <p>{brand.tagline}</p>
          </section>
        )}

        <div className="lookup-sentinel" aria-hidden="true" />
        <section className="lookup-dock" aria-label="CTCAE search controls">
          <div className="version-control-wrap">
            <span>CTCAE Version</span>
            <div className="version-control" data-version={version} role="group" aria-label="CTCAE Version">
              {(["5.0", "6.0"] as CtcaeVersion[]).map((item) => (
                <button type="button" key={item} className={version === item ? "active" : ""} aria-pressed={version === item} onClick={() => changeVersion(item)}>v{item}</button>
              ))}
            </div>
          </div>
          <div className="search-wrap" ref={searchWrapRef}>
            <SearchStatusIndicator status={effectiveSearchIndicator} />
            <input
              ref={searchRef}
              type="search"
              inputMode="search"
              value={query}
              placeholder="Search symptom, diagnosis, lab, or CTCAE term…"
              aria-label="Search CTCAE"
              aria-controls="search-results"
              aria-expanded={resultsOpen && Boolean(results.length)}
              onFocus={() => setResultsOpen(true)}
              onChange={(event) => {
                const nextQuery = event.target.value;
                setVersionSwitchTerm(undefined);
                searchRecorderRef.current?.schedule(nextQuery, version);
                const currentEncoderQuery = encoderQueryKey(query);
                const nextEncoderQuery = encoderQueryKey(nextQuery);
                const encoderQueryChanged = nextEncoderQuery !== currentEncoderQuery;
                const willRunEncoder = encoderQueryChanged
                  && Boolean(semanticIndexes[version])
                  && nextEncoderQuery.length >= 2;
                setQuery(nextQuery);
                if (encoderQueryChanged) {
                  latestSemanticRequest.current = 0;
                  setSemanticMatches([]);
                  setSearchIndicator((current) => {
                    if (current.phase === "waiting" || current.phase === "downloading" || current.phase === "load-error") {
                      return current;
                    }
                    return willRunEncoder
                      ? { phase: "debouncing", label: "Smart search is waiting to run." }
                      : { phase: "idle" };
                  });
                  if (willRunEncoder) setSemanticStatus("Waiting to run smart search…");
                }
                setMeasurement(undefined);
                setResultsOpen(true);
                setActiveResult(0);
              }}
              onKeyDown={onSearchKeyDown}
              autoComplete="off"
              maxLength={500}
            />
            {query && <button className="clear-button" type="button" onClick={clearSearch} aria-label="Clear search" />}
          </div>
          {!hasResultContext && <p className="search-hint">Try “fatigue”, “low platelets”, or “anemia; 7.6 g/dL”. <span role="status">{semanticStatus}</span></p>}

          {resultsOpen && query && results.length > 0 && (
            <div ref={searchResultsRef} className="search-results" id="search-results" role="listbox" aria-label="CTCAE search results">
              {results.map((result, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={activeResult === index}
                  className={activeResult === index ? "active" : ""}
                  key={result.record.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveResult(index)}
                  onClick={() => {
                    searchRecorderRef.current?.select(query, version, result);
                    selectRecord(result.record, result.matchType, true);
                  }}
                >
                  <span><strong>{result.record.term}</strong><small>{result.record.soc}</small></span>
                  <em>{matchLabel(result.matchType)}</em>
                </button>
              ))}
            </div>
          )}
        </section>

        {loading && !currentRecords.length && <div className="loading-state"><span />Loading CTCAE v{version} source data…</div>}
        {error && <div className="error-state"><strong>Source data did not load.</strong><p>{error}</p><button type="button" onClick={() => window.location.reload()}>Try again</button></div>}

        {unavailableTerm && (
          <section className="no-results version-unavailable" role="status">
            <span aria-hidden="true">⌕</span>
            <small>Selected term</small>
            <h2>{unavailableTerm.term}</h2>
            <p>This term is not available in CTCAE v{version}.</p>
            <button type="button" className="primary-button" onClick={() => setBrowseOpen(true)}>Browse CTCAE v{version} categories</button>
          </section>
        )}

        {!selected && !versionSwitchTerm && !loading && query && !results.length && (
          <section className="no-results">
            <span aria-hidden="true">⌕</span>
            <h2>No confident CTCAE match found</h2>
            <p>Try another term or browse by System Organ Class.</p>
            <button type="button" className="primary-button" onClick={() => setBrowseOpen(true)}>Browse CTCAE categories</button>
          </section>
        )}

        {!query && !selected && currentRecords.length > 0 && (
          <section className="home-sections">
            <button className="browse-button" type="button" onClick={() => setBrowseOpen(true)}><span><small>Explore all {currentRecords.length} terms</small>Browse by System Organ Class</span><b>›</b></button>
            {savedRecords.length > 0 && <HomeList title="Saved" records={savedRecords} onSelect={selectRecord} />}
            {recentRecords.length > 0 && <HomeList title="Recent" records={recentRecords} onSelect={selectRecord} />}
            {!savedRecords.length && !recentRecords.length && (
              <div className="empty-home"><span aria-hidden="true">⌕</span><h2>Your field reference is ready</h2><p>Search above. Searches are logged without user identifiers to improve future retrieval.</p></div>
            )}
          </section>
        )}

        {selected && (
          <ResultDetail
            record={selected}
            matchType={effectiveMatch}
            parsedQuery={gradingQuery}
            evaluation={evaluation}
            references={references}
            onReferenceValue={(key: ReferenceKey, value?: number) => setReferences((current) => ({ ...current, [key]: value }))}
            onValueChange={(value, unit) => setMeasurement(value === undefined ? undefined : { value, unit })}
            saved={isSaved}
            onToggleSaved={toggleCurrentSaved}
            onOpenReference={openReference}
            onOpenPdf={() => setPdfTarget({
              document: selected.source.document,
              page: selected.source.page,
              title: `CTCAE v${selected.version} — ${selected.term}`,
            })}
          />
        )}

        <section className="grading-help">
          <div className="grading-help-panel">
            <h2>General CTCAE v{version} grading help</h2>
            <ol>{generalGrades[version].map((text, index) => <li key={text}><strong>Grade {index + 1}</strong><p>{text}</p></li>)}</ol>
            <button
              className="grading-source-button"
              type="button"
              onClick={() => setPdfTarget({
                document: version === "5.0" ? "ctcae-v5.pdf" : "ctcae-v6.pdf",
                page: 2,
                title: `CTCAE v${version} — Introduction`,
              })}
            >
              View the source introduction
            </button>
          </div>
        </section>
      </main>

      <footer id="about">
        <strong>{brand.organization} · {brand.product}</strong>
        <p>CTCAE terminology and grading criteria displayed by this tool are derived from the supplied NCI CTCAE v5.0 and v6.0 reference documents. This tool is intended to facilitate CTCAE search and does not replace the source documents or clinical judgment.</p>
        <span>No accounts · No patient profiles · Search events are logged without user identifiers · Do not enter patient-identifying information</span>
      </footer>

      <BrowsePanel records={currentRecords} open={browseOpen} onClose={() => setBrowseOpen(false)} onSelect={selectRecord} />
      <TermDialog
        history={referenceRecords}
        onBack={() => setReferenceHistory((history) => history.slice(0, -1))}
        onClose={closeReferences}
        onOpenReference={openReference}
        onOpenPdf={(record) => setPdfTarget({
          document: record.source.document,
          page: record.source.page,
          title: `CTCAE v${record.version} — ${record.term}`,
        })}
      />
      <PdfModal target={pdfTarget} onClose={closePdf} />
    </div>
  );
}

function HomeList({ title, records, onSelect }: { title: string; records: CtcaeTerm[]; onSelect: (record: CtcaeTerm) => void }) {
  return (
    <section className="home-list">
      <div className="home-list-title"><span className="section-kicker">Quick access</span><h2>{title}</h2></div>
      <div>{records.slice(0, 8).map((record) => <button type="button" key={record.id} onClick={() => onSelect(record)}><span><strong>{record.term}</strong><small>{record.soc}</small></span><em>v{record.version}</em></button>)}</div>
    </section>
  );
}
